import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

function useToken() {
  const { getToken } = useAuth();
  return async () => ({ headers: { Authorization: `Bearer ${await getToken()}` } });
}

export function AdminCustomers() {
  const h = useToken();
  const [data, setData] = useState({ users: [], restricted: [] });
  useEffect(() => { (async () => setData((await api.get("/admin/customers", await h())).data))(); }, []);
  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">People</p>
      <h1 className="font-serif text-4xl mt-2 mb-10">Customers</h1>
      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#5E4D5A] border-b border-[#A8B59A]/20"><th className="p-4">User ID</th><th className="p-4">Role</th><th className="p-4">Orders</th></tr></thead>
          <tbody>{data.users.map((u) => <tr key={u.user_id} className="border-b border-[#A8B59A]/10"><td className="p-4 font-mono text-xs">{u.user_id}</td><td className="p-4">{u.role}</td><td className="p-4">{u.orders}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAnnouncements() {
  const h = useToken();
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");

  const load = async () => setList((await api.get("/announcements")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!msg.trim()) return;
    await api.post("/admin/announcements", { message: msg, active: true }, await h());
    toast.success("Announcement published");
    setMsg(""); load();
  };
  const del = async (id) => {
    await api.delete(`/admin/announcements/${id}`, await h());
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Site</p>
      <h1 className="font-serif text-4xl mt-2 mb-10">Announcements</h1>
      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 mb-10 flex gap-3">
        <Input data-testid="ann-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="e.g., Holiday closure Dec 23–26" className="rounded-xl" />
        <Button data-testid="ann-publish" onClick={create} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white"><Plus size={14} className="mr-1" />Publish</Button>
      </div>
      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-[#A8B59A]/20 p-5 flex justify-between items-center">
            <span>{a.message}</span>
            <button onClick={() => del(a.id)} className="text-[#59394F]"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminRestrictions() {
  const h = useToken();
  const [data, setData] = useState({ restricted: [] });
  const [form, setForm] = useState({ email: "", reason: "" });

  const load = async () => setData((await api.get("/admin/customers", await h())).data);
  useEffect(() => { load(); }, []);

  const block = async () => {
    if (!form.email || !form.reason) return toast.error("Email and reason required");
    await api.post("/admin/restrictions", form, await h());
    toast.success("Blocked");
    setForm({ email: "", reason: "" }); load();
  };
  const unblock = async (email) => {
    await api.delete(`/admin/restrictions/${encodeURIComponent(email)}`, await h());
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Protection</p>
      <h1 className="font-serif text-4xl mt-2 mb-10">Restrictions</h1>
      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 mb-10 grid md:grid-cols-3 gap-3">
        <Input data-testid="rst-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" />
        <Input data-testid="rst-reason" placeholder="Internal reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-xl" />
        <Button data-testid="rst-block" onClick={block} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white">Block</Button>
      </div>
      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#5E4D5A] border-b border-[#A8B59A]/20"><th className="p-4">Email</th><th className="p-4">Reason</th><th className="p-4">Since</th><th className="p-4"></th></tr></thead>
          <tbody>{data.restricted.map((r) => (
            <tr key={r.email} className="border-b border-[#A8B59A]/10">
              <td className="p-4">{r.email}</td><td className="p-4 text-[#5E4D5A]">{r.reason}</td>
              <td className="p-4 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="p-4"><button onClick={() => unblock(r.email)} className="text-xs text-[#59394F] hover:underline">unblock</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
